import { DestroyRef, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormControl } from '@angular/forms';
import { Dayjs } from 'dayjs';
import { RoleType } from '../../../../services/RoleModel';
import { PersonEditorRoleValue, StrandContext, StrandNeighbour } from './person-editor-dialog.types';
import { StrandContextFactory } from './strand-context.factory';

/** Grenzlogik zwischen Rolle und Vorgänger; hält ein Control je Nachbar und wird deshalb je Dialog bereitgestellt. */
@Injectable()
export class StrandBoundaryService {
	private readonly fb = inject(FormBuilder);
	private readonly destroyRef = inject(DestroyRef);

	private readonly controls = new Map<string, FormControl<Dayjs | null>>();
	private strands: StrandContext[] = [];

	/** Zählt jede Grenzänderung — Verbraucher hängen ihre `computed`s daran. */
	readonly revision = signal(0);

	init(strands: StrandContext[]): void {
		this.strands = strands;
	}

	reset(strands: StrandContext[]): void {
		this.controls.clear();
		this.init(strands);
		this.revision.update((value) => value + 1);
	}

	/** Im Belegungsstrang ergibt sich das Ende aus dem Einzug; frei setzbar entstünden Lücken. */
	isEditable(strandType: RoleType): boolean {
		return !StrandContextFactory.isOccupancyStrand(strandType);
	}

	/** Bis-Feld der Kontextzeile; vorbelegt mit dem heute hinterlegten Ende des Nachbarn. */
	control(roleId: string): FormControl<Dayjs | null> {
		const existing = this.controls.get(roleId);
		if (existing) {
			return existing;
		}
		const control = this.fb.control<Dayjs | null>(this.neighbourById(roleId)?.validTo ?? null);
		control.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.revision.update((value) => value + 1));
		this.controls.set(roleId, control);
		return control;
	}

	strandFor(roleType: RoleType | null): StrandContext | undefined {
		if (!roleType) {
			return undefined;
		}
		return this.strands.find((strand) => strand.roleType === StrandContextFactory.strandTypeOf(roleType));
	}

	/** Frühestes wählbares Von: der Tag nach dem Beginn des Vorgängers. */
	minValidFrom(roleType: RoleType | null): Dayjs | null {
		const previous = this.strandFor(roleType)?.previous;
		return previous ? previous.validFrom.add(1, 'day') : null;
	}

	/** Spätestes wählbares Von: der Tag vor dem Beginn des Nachfolgers. */
	maxValidFrom(roleType: RoleType | null): Dayjs | null {
		const next = this.strandFor(roleType)?.next;
		return next ? next.validFrom.subtract(1, 'day') : null;
	}

	/** Zieht die Vorgängergrenze mit dem Einzug — nur im Belegungsstrang sind beide ein Wert. */
	pull(roleType: RoleType | null, date: Dayjs | null): void {
		if (!date || !StrandContextFactory.isOccupancyStrand(roleType)) {
			return;
		}
		const previous = this.strandFor(roleType)?.previous;
		if (previous) {
			this.control(previous.roleId).setValue(date.subtract(1, 'day'), { emitEvent: false });
			this.revision.update((value) => value + 1);
		}
	}

	/** Nur Grenzen, die vom nahtlosen Default abweichen, müssen geschrieben werden. */
	overrides(roles: PersonEditorRoleValue[]): Map<string, Dayjs> {
		const result = new Map<string, Dayjs>();
		for (const [roleId, control] of this.controls) {
			const value = control.value;
			const seamless = this.seamlessEnd(roleId, roles);
			if (value && (!seamless || !value.isSame(seamless, 'day'))) {
				result.set(roleId, value);
			}
		}
		return result;
	}

	private neighbourById(roleId: string): StrandNeighbour | undefined {
		return this.strands.flatMap((strand) => [strand.previous, strand.next]).find((neighbour) => neighbour?.roleId === roleId);
	}

	/** Das Ende, das das Backend beim Anlegen selbst setzen würde: Beginn der neuen Zeile − 1 Tag. */
	private seamlessEnd(roleId: string, roles: PersonEditorRoleValue[]): Dayjs | null {
		const strand = this.strands.find((entry) => entry.previous?.roleId === roleId);
		if (!strand) {
			return this.neighbourById(roleId)?.validTo ?? null;
		}
		const earliest = roles
			.filter((role) => !role.id && !!role.roleType && StrandContextFactory.strandTypeOf(role.roleType) === strand.roleType)
			.map((role) => role.validFrom)
			.filter((validFrom): validFrom is Dayjs => !!validFrom)
			.sort((a, b) => a.valueOf() - b.valueOf())[0];
		return earliest ? earliest.subtract(1, 'day') : (strand.previous?.validTo ?? null);
	}
}
